// @wc-ignore-file
/**
 * CR-69: how a `ViaAgent` slug is named to a user.
 *
 * KEEP IN SYNC with backend/api-server/src/lib/via-agent-labels.ts. `shared/`
 * is types-only in this repo (tsc does not rewrite the `@shared/*` alias in
 * emitted JS), so the runtime map is copied rather than imported — the same
 * arrangement as the three copies of mime-types.
 *
 * `@wc-ignore-file` is load-bearing, not tidiness: wuchale extracts string
 * literals from plain `.ts` files and machine-translates them, which would turn
 * "Agent (MCP)" into a localised word and make the label disagree across
 * surfaces. The surrounding sentence ("<label> · via <name>") IS translated —
 * it lives in the JSX at each render site.
 *
 * Unknown slugs are absent from the map ON PURPOSE. `via_agent` is a `text`
 * column, so a client can meet a value shipped after it was built; resolving to
 * `undefined` makes every render site fall back to the plain actor name instead
 * of printing a raw slug or asserting a channel it does not know.
 */
import type { ViaAgent } from "@shared/types/via-agent";

/**
 * `claude-mcp` names the CHANNEL, not an agent: "Agent (MCP)", never "Claude".
 * The kairo-mcp server cannot know which client holds a PERSONAL API key —
 * Claude, Codex, OpenCode and raw curl all present the same one — so any agent
 * name would be a guess that mislabels every non-Claude client (Éric,
 * 2026-09-02). What the marker guarantees is the part it states: this write
 * came through the MCP on behalf of the named person, not from the person
 * directly and not from Kairo's own in-app assistant.
 *
 * `claude-agent-key` (CR-111) names an agent on purpose. An agent API key is
 * not a human credential a tool borrowed: it is created, named, scoped to one
 * workspace or project and revoked as an agent identity, by the person who
 * remains accountable for it. Éric chose the wording (definition review Q7,
 * 2026-09-11); V1 keeps one fixed label per value rather than rendering the
 * key's own name.
 *
 * The slugs stay as they are for history: they are stored values on existing
 * notifications/messages/cr_events rows, and renaming one would orphan them.
 */
export const VIA_AGENT_LABELS: Record<ViaAgent, string> = {
  "claude-mcp": "Agent (MCP)",
  "claude-agent-key": "Claude · Agent (MCP)",
  // CR-189 S4: the release workflow's user guide sync.
  "release-sync": "Release sync",
};

/** The display label for a stored `via_agent`, or undefined when unrecognised. */
export function viaAgentLabel(viaAgent: string | null | undefined): string | undefined {
  if (!viaAgent) return undefined;
  return VIA_AGENT_LABELS[viaAgent as ViaAgent];
}
